//@import dependencies
import React, {useState, useEffect} from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
//@end

//@import components
import AvatarComponent from 'app_components/commons/avatar'
import ReactSelectComponent from 'app_components/commons/reactSelect'
//@end

// @import hooks
// @end

// @INFO Import types
// @end

// @INFO Import services
import UserService from 'app_services/user/userService'
import TimezoneService from 'app_services/user/timezoneService'
import RoleService from 'app_services/admin/role/roleService'
// @end

// @INFO Import Styles
import './../userFormModule.scss'
import { UserFormInputs } from 'app_types/admin/user/userTypes'
import { ITimeZoneOption } from 'app_services/user/zones'

// @ADD Component types
interface IUserFormModuleLayoutProps {
  onHandleSubmit: (data: any) => void
  editData?: any
  viewer: boolean
  role?: string | undefined
  show_inputs?: UserFormInputs[]
  editabled_inputs?: UserFormInputs[]
}

const UserFormModuleLayout: React.FC<IUserFormModuleLayoutProps> = (props) => {

  // @INFO: Services
  const timeZoneService: TimezoneService = new TimezoneService()
  const roleService: RoleService = new RoleService()

  const { t } = useTranslation()
  const userService = new UserService()
  const [showInputs, setShowInputs] = useState<UserFormInputs[]>([])
  const [editabledInputs, setEditabledInputs] = useState<UserFormInputs[]>([])
  const formHook = useForm()
  const [listOfTimeZones, setListOfTimeZones] = useState<Array<any>>([])
  const [listOfCultures, setListOfCultures] = useState<Array<any>>([])
  const [listOfRoles, setListOfRoles] = useState<Array<any>>([])

  const [showPosition, updateShowPosition] = useState<boolean>(false);

  const rolesSpy = formHook.watch('roles');

  const getShowInputs = () => {
    let inputsByDefault =  [
      UserFormInputs.FIRST_NAME,
      UserFormInputs.LAST_NAME,
      UserFormInputs.USERNAME,
      UserFormInputs.EMAIL,
      UserFormInputs.PHONE_NUMBER,
      UserFormInputs.DOC_NUMBER,
      UserFormInputs.DOC_TYPE,
      UserFormInputs.PROGRAMMING_CODE,
      UserFormInputs.PASSWORD,
      UserFormInputs.TIMEZONE,
      UserFormInputs.DIALECT,
      UserFormInputs.ROLES,
    ]
    if (props.show_inputs) inputsByDefault = props.show_inputs

    const inputs = []
    for (const input of inputsByDefault) {
      if (userService.can(`permission:users_field_${input}`, 'module:users')) {
        inputs.push(input)
      }
    }
    setShowInputs(inputs);
  }

  const getEditableInputs = () => {
    let inputsByDefault = [
      UserFormInputs.FIRST_NAME,
      UserFormInputs.LAST_NAME,
      UserFormInputs.USERNAME,
      UserFormInputs.EMAIL,
      UserFormInputs.PHONE_NUMBER,
      UserFormInputs.DOC_NUMBER,
      UserFormInputs.DOC_TYPE,
      UserFormInputs.PROGRAMMING_CODE,
      UserFormInputs.PASSWORD,
      UserFormInputs.TIMEZONE,
      UserFormInputs.DIALECT,
      UserFormInputs.ROLES,
    ]
    if (props.editabled_inputs) inputsByDefault = props.editabled_inputs

    const inputs = []
    for (const input of inputsByDefault) {
      if (userService.can(`permission:users_field_${input}`, 'module:users')) {
        inputs.push(input)
      }
    }
    setEditabledInputs(inputs)
  }

  useEffect(() => {
    getShowInputs()
    getEditableInputs()
  }, [props.show_inputs, props.editabled_inputs])

  useEffect(() => {
    if (listOfTimeZones.length === 0) {
      setListOfTimeZones(timeZoneService.getAllZones())
    }
  }, [timeZoneService, listOfTimeZones])

  useEffect(() => {
    if (listOfCultures.length === 0) {
      setListOfCultures(timeZoneService.getAllCultures())
    }
  }, [timeZoneService, listOfCultures])

  useEffect(() => {
    if (listOfRoles.length === 0) {
      getRoles()
    }
  }, [listOfRoles])

  useEffect(() => {
    if (rolesSpy?.length && rolesSpy.find((r: any) => r.label === 'Compañia')) {
      updateShowPosition(true);
    } else {
      updateShowPosition(false);
    }
  }, [rolesSpy])

  const getRoles = async () => {
    const roles: Array<any> = []
    const rolesResponse = await roleService.getRoles({})
    if (rolesResponse) {
      rolesResponse.roles.map((_role: any) => {
        if (!props.role || (props.role && props.role === _role.name)) {
          roles.push({ value: _role._id, label: _role.description })
        }
      })
    }
    setListOfRoles(roles)
  }

  const isReadOnly = (field: UserFormInputs) => {
    let readOnly = false;
    switch(field) {
      case UserFormInputs.USERNAME:
        readOnly = props?.editData;
        break;
      case UserFormInputs.PASSWORD:
      case UserFormInputs.FIRST_NAME:
      case UserFormInputs.LAST_NAME:
      case UserFormInputs.TIMEZONE:
      case UserFormInputs.DIALECT:
      case UserFormInputs.ROLES:
      case UserFormInputs.DOC_NUMBER:
        if (!props?.editData) {
          readOnly = false;
        } else {
          if (!editabledInputs?.includes(field)) {
            readOnly = true;
          }
        }
        break;
      case UserFormInputs.EMAIL:
      case UserFormInputs.PHONE_NUMBER:
      case UserFormInputs.DOC_TYPE:
      case UserFormInputs.PROGRAMMING_CODE:
        if (!editabledInputs?.includes(field)) {
          readOnly = true;
        }
        break;

    }
    return readOnly;
  }

  const isRequired = (field: UserFormInputs) => {
    let required = false;
    switch(field) {
      case UserFormInputs.USERNAME:
        required = !props?.editData;
        break;
      case UserFormInputs.PASSWORD:
        if (!props?.editData) {
          required = true;
        }
        break;
      case UserFormInputs.FIRST_NAME:
      case UserFormInputs.LAST_NAME:
      case UserFormInputs.TIMEZONE:
      case UserFormInputs.DIALECT:
      case UserFormInputs.ROLES:
      case UserFormInputs.DOC_NUMBER:
        if (!props?.editData) {
          required = true;
        } else {
          if (editabledInputs?.includes(field)) {
            required = true;
          }
        }
        break;
      case UserFormInputs.EMAIL:
      case UserFormInputs.PHONE_NUMBER:
      case UserFormInputs.DOC_TYPE:
      case UserFormInputs.PROGRAMMING_CODE:
        if (editabledInputs?.includes(field)) {
          required = true;
        }
        break;
    }
    return required;
  }

  return(
    <div className='user-form'>

      <fieldset disabled={props.viewer ? true : false}>
        <form onSubmit={formHook.handleSubmit(props.onHandleSubmit)}>

          <div className="avatar-container">
            <AvatarComponent
              src={props.editData?.profile?.avatarImageUrl || ''}
              size="5rem"
            />
            <div className={`row`}>
              <div className="col-md-12">
                <div className='form-group'>
                  {formHook.errors.avatar && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                  <input
                    id='avatar_input'
                    name='avatar'
                    className='form-control-file'
                    type='file'
                    ref={formHook.register({ required: false })}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center mb-2">
            <h3 className="form-title">{t("modules.users.general_information")}</h3>
          </div>
          <div className="row">
            <div className="col-md-6">
              <div className='form-group '>
                <label htmlFor='first_name'>{t('labels.name')} {isRequired(UserFormInputs.FIRST_NAME) ? '*' : ''}</label>
                {formHook.errors.profile?.first_name && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                <input
                  id='first_name'
                  name='profile[first_name]'
                  className={`form-control ${formHook.errors.profile?.first_name && 'is-invalid'}`}
                  type='text'
                  ref={formHook.register({ required: isRequired(UserFormInputs.FIRST_NAME) })}
                  defaultValue={props?.editData?.details?.profile?.first_name}
                  readOnly={isReadOnly(UserFormInputs.FIRST_NAME)}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className='form-group '>
                <label htmlFor='last_name'>{t('labels.last_name')} {isRequired(UserFormInputs.LAST_NAME) ? '*' : ''}</label>
                {formHook.errors.profile?.last_name && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                <input
                  id='last_name'
                  name='profile[last_name]'
                  className={`form-control ${formHook.errors.profile?.last_name && 'is-invalid'}`}
                  type='text'
                  ref={formHook.register({ required: isRequired(UserFormInputs.LAST_NAME) })}
                  defaultValue={props?.editData?.details?.profile?.last_name}
                  readOnly={isReadOnly(UserFormInputs.LAST_NAME)}
                />
              </div>
            </div>
            {showInputs?.includes(UserFormInputs.EMAIL) ? (
              <div className="col-md-6">
                <div className='form-group '>
                  <label htmlFor='email'>{t('labels.email')} {isRequired(UserFormInputs.EMAIL) ? '*' : ''}</label>
                  {formHook.errors.email && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                  <input
                    id='email'
                    name='email'
                    className={`form-control ${formHook.errors.email && 'is-invalid'}`}
                    type='email'
                    ref={formHook.register({
                      required: isRequired(UserFormInputs.EMAIL)
                    })}
                    defaultValue={props?.editData?.details?.email}
                    readOnly={isReadOnly(UserFormInputs.EMAIL)}
                  />
                </div>
              </div>
            ): null}
            {showInputs?.includes(UserFormInputs.PHONE_NUMBER) ? (
              <div className="col-md-6">
                <div className='form-group '>
                  <label htmlFor='phone'>{t('labels.phone_number')} {isRequired(UserFormInputs.PHONE_NUMBER) ? '*' : ''}</label>
                  {formHook.errors?.phoneNumber && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                  <input
                    id='phoneNumber'
                    name='phoneNumber'
                    className={`form-control ${formHook.errors?.phoneNumber && 'is-invalid'}`}
                    type='number'
                    min='0'
                    ref={formHook.register({
                      required: isRequired(UserFormInputs.PHONE_NUMBER)
                    })}
                    defaultValue={props?.editData?.details?.phoneNumber}
                    readOnly={isReadOnly(UserFormInputs.PHONE_NUMBER)}
                  />
                </div>
              </div>
            ): null}
            {showInputs?.includes(UserFormInputs.DOC_NUMBER) ? (
              <div className="col-md-6">
                <div className='form-group '>
                  <label htmlFor='doc_number'>{t('labels.doc_number')} {isRequired(UserFormInputs.DOC_NUMBER) ? '*' : ''}</label>
                  {formHook.errors?.profile?.doc_number && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                  <input
                    id='doc_number'
                    name='profile[doc_number]'
                    className={`form-control ${formHook.errors?.profile?.doc_number && 'is-invalid'}`}
                    ref={formHook.register({
                      required: isRequired(UserFormInputs.DOC_NUMBER)
                    })}
                    defaultValue={props?.editData?.details?.profile?.doc_number}
                    readOnly={isReadOnly(UserFormInputs.DOC_NUMBER)}
                  />
                </div>
              </div>
            ): null}
            {showInputs?.includes(UserFormInputs.DOC_TYPE) ? (
              <div className="col-md-6">
                <div className='form-group '>
                  <label htmlFor='doc_type'>{t('labels.doc_type')} {isRequired(UserFormInputs.DOC_TYPE) ? '*' : ''}</label>
                  {formHook.errors?.profile?.doc_type && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                  <input
                    id='doc_type'
                    name='profile[doc_type]'
                    className={`form-control ${formHook.errors?.profile?.doc_type && 'is-invalid'}`}
                    ref={formHook.register({
                      required: isRequired(UserFormInputs.DOC_TYPE)
                    })}
                    defaultValue={props?.editData?.details?.profile?.doc_type}
                    readOnly={isReadOnly(UserFormInputs.DOC_TYPE)}
                  />
                </div>
              </div>
            ): null}
          </div>

          <div className="d-flex align-items-center mb-2">
            <h3 className="form-title">{t("modules.users.account_information")}</h3>
          </div>
          <div className="row">
            <div className="col-md-6">
              <div className='form-group '>
                <label htmlFor='username'>{t('labels.user_name')} {isRequired(UserFormInputs.USERNAME) ? '*' : ''}</label>
                {formHook.errors.username && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                <input
                  id='username'
                  name='username'
                  className={`form-control ${formHook.errors.username && 'is-invalid'}`}
                  type='text'
                  ref={formHook.register({
                    required: isRequired(UserFormInputs.USERNAME)
                  })}
                  defaultValue={props?.editData?.details?.username}
                  readOnly={isReadOnly(UserFormInputs.USERNAME)}
                />
              </div>
            </div>
            {showInputs?.includes(UserFormInputs.PROGRAMMING_CODE) ? (
              <div className="col-md-6">
                <div className='form-group '>
                  <label htmlFor='short_key'>{t('labels.programming_code')} {isRequired(UserFormInputs.PROGRAMMING_CODE) ? '*' : ''}</label>
                  {formHook.errors.short_key && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                  <input
                    id='short_key'
                    name='short_key'
                    className={`form-control ${formHook.errors.short_key && 'is-invalid'}`}
                    type='short_key'
                    ref={formHook.register({
                      required: isRequired(UserFormInputs.PROGRAMMING_CODE)
                    })}
                    defaultValue={props?.editData?.details?.short_key}
                    readOnly={isReadOnly(UserFormInputs.PROGRAMMING_CODE)}
                  />
                </div>
              </div>
            ): null}
            <div className="col-md-6">
              <div key={props?.editData?.details?._id} className='form-group '>
                <label htmlFor='timezone'>{t('labels.time_zone')} {isRequired(UserFormInputs.TIMEZONE) ? '*' : ''}</label>
                {formHook.errors.profile?.timezone && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                <Controller
                  control={formHook.control}
                  defaultValue={props?.editData?.details?.profile?.timezone}
                  name='profile[timezone]'
                  render={({ ref, value, onChange }) => (
                    <select
                      id='timezone'
                      className={`form-control ${formHook.errors.timezone && 'is-invalid'}`}
                      disabled={isReadOnly(UserFormInputs.TIMEZONE)}
                      value={value}
                      ref={ref}
                      onChange={(e) => onChange(e.target.value)}
                    >
                      {listOfTimeZones.map((timezone: ITimeZoneOption) => {
                        return (
                          <option value={timezone.value} key={timezone.value}>
                            {timezone.label}
                          </option>
                        )
                      })}
                    </select>
                  )}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className='form-group '>
                <label htmlFor='culture'>{t('labels.dialect')} {isRequired(UserFormInputs.DIALECT) ? '*' : ''}</label>
                {formHook.errors.profile_culture && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                <select
                  id='culture'
                  name='profile[culture]'
                  className={`form-control ${formHook.errors.profile_culture && 'is-invalid'}`}
                  ref={formHook.register({
                    required: isRequired(UserFormInputs.DIALECT)
                  })}
                  defaultValue={props?.editData?.details?.profile?.culture}
                  disabled={isReadOnly(UserFormInputs.DIALECT)}
                >
                  {listOfCultures.map((culture: any, i: number) => {
                    return (
                      <option value={culture.value} key={i}>
                        {culture.label}
                      </option>
                    )
                  })}
                </select>
              </div>
            </div>
            <div className="col-md-6">
              <div className='form-group '>
                <label>{t("modules.users.form.roles")} {isRequired(UserFormInputs.ROLES) ? '*' : ''}</label>
                <ReactSelectComponent
                  identifier='roles'
                  options={listOfRoles}
                  formHooks={formHook}
                  defaultValue={(props?.editData?.details?.roles) ? props?.editData?.details?.roles.map((r: any) => {return {value: r._id, label: r.description}}) : []}
                  isMulti={true}
                  required={isRequired(UserFormInputs.ROLES)}
                  isDisabled={isReadOnly(UserFormInputs.ROLES)}
                />
                {formHook.errors?.roles && <div className='invalid-feedback' style={{display: 'flex'}}>{t('errors.this_field_is_required')}</div>}
              </div>
            </div>
            {showPosition ?
              <div className="col-md-6">
                <div className='form-group '>
                  <label htmlFor='currentPosition'>{t('labels.currentPosition')}</label>
                  {formHook.errors.profile?.currentPosition && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                  <input
                    id='currentPosition'
                    name='profile[currentPosition]'
                    className={`form-control ${formHook.errors.profile?.currentPosition && 'is-invalid'}`}
                    type='text'
                    ref={formHook.register({ required: true })}
                    defaultValue={props?.editData?.details?.profile?.currentPosition}
                  />
                </div>
              </div>
            :formHook.unregister('currentPosition')}
          </div>

          {showInputs?.includes(UserFormInputs.PASSWORD) ? (
            <>
              <div className="d-flex align-items-center mb-2">
                <h3 className="form-title">{t("modules.users.security")}</h3>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className={`form-group  ${props.editData ? '' : 'required'}`}>
                    <label htmlFor='password'>{t('labels.password')} {isRequired(UserFormInputs.PASSWORD) ? '*' : ''}</label>
                    {formHook.errors.password && <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>}
                    <input
                      id='password'
                      name='password'
                      type='password'
                      className={`form-control ${formHook.errors.password && 'is-invalid'}`}
                      ref={formHook.register({
                        required: isRequired(UserFormInputs.PASSWORD)
                      })}
                      disabled={isReadOnly(UserFormInputs.PASSWORD)}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className={`form-group  ${props.editData ? '' : 'required'}`}>
                    <label htmlFor='confirm_password'>{t('labels.password_repeat')}</label>
                    {formHook.errors.confirm_password && <div className='invalid-feedback'>Las contraseñas no coinciden</div>}
                    <input
                      id='confirm_password'
                      type='password'
                      name='confirm_password'
                      className={`form-control ${formHook.errors.confirm_password && 'is-invalid'}`}
                      ref={formHook.register({
                        validate: (value: any) => value === formHook.watch('password'),
                      })}
                      disabled={isReadOnly(UserFormInputs.PASSWORD)}
                    />
                  </div>
                </div>
              </div>
            </>
          ): null}

          {!props.viewer && (
            <div className='buttons-group'>
              <div className='text-right'>
                <button className='btn btn-primary' type='submit'>
                  {t('buttons.save')}
                </button>
              </div>
            </div>
          )}
        </form>
      </fieldset>
    </div>
  )
}

export default UserFormModuleLayout
